import React, { useEffect, useState } from 'react';
import Cookie from 'js-cookie';
import { BASE_URL } from '../../config/constants';
import { useTranslation } from 'react-i18next';

function AddTeacher() {
	const { t } = useTranslation();
	const [teacherData, setTeacherData] = useState({
		name: '',
		description: '',
		photoId: '',
	});

	const [file, setFile] = useState(null);
	const [teachers, setTeachers] = useState([]);
	const [teacherId, setTeacherId] = useState('');
	const [isEditing, setIsEditing] = useState(false);
	const baseUrl = BASE_URL;

	const handleInputsValue = (event) => {
		const { name, value } = event.target;
		setTeacherData({
			...teacherData,
			[name]: value,
		});
	};

	const handleFileChange = (event) => {
		setFile(event.target.files[0]);
	};

	const handleSubmitForm = async (event) => {
		event.preventDefault();

		// Remove the file requirement for editing
		if (!file && !isEditing && !teacherData.photoId) {
			alert('Please select a file for new teacher!');
			return;
		}

		try {
			let fileData = null;

			// Only upload file if a new file is selected
			if (file) {
				const formData = new FormData();
				formData.append('media', file);

				console.log('📤 Uploading file:', file);

				const fileResponse = await fetch(`${baseUrl}/api/v1/media/upload`, {
					method: 'POST',
					body: formData,
					headers: {
						Authorization: `Bearer ${Cookie.get('token')}`,
					},
				});

				console.log('📥 File upload response status:', fileResponse.status);

				if (!fileResponse.ok) {
					const errorData = await fileResponse.json();
					console.error('❌ File upload failed:', errorData);
					alert(`File upload failed: ${errorData.message || 'Unknown error'}`);
					return;
				}

				fileData = await fileResponse.json();
				console.log('✅ File uploaded successfully:', fileData);
			}

			// FIX: Use the correct property from the response
			const updatedTeacherData = {
				...teacherData,
				// Use fileData.key or fileData.id depending on what the teacher API expects
				// Based on the API doc, it expects a UUID string for photoId
				photoId: fileData ? fileData.id || fileData.key : teacherData.photoId,
			};

			// Debug log before sending
			console.log('📤 Sending teacher data:', updatedTeacherData);
			console.log(
				'🌍 API Endpoint:',
				isEditing
					? `${baseUrl}/api/v1/teacher/${teacherId}`
					: `${baseUrl}/api/v1/teacher`
			);

			// Validate required fields
			if (!updatedTeacherData.name.trim()) {
				alert('Teacher name is required');
				return;
			}

			const teacherResponse = await fetch(
				isEditing
					? `${baseUrl}/api/v1/teacher/${teacherId}`
					: `${baseUrl}/api/v1/teacher`,
				{
					method: isEditing ? 'PATCH' : 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${Cookie.get('token')}`,
					},
					body: JSON.stringify(updatedTeacherData),
				}
			);

			console.log('📥 Teacher response status:', teacherResponse.status);

			if (teacherResponse.ok) {
				const responseData = await teacherResponse.json();
				console.log('✅ Teacher saved successfully:', responseData);

				alert(
					isEditing
						? 'Teacher updated successfully'
						: 'Teacher added successfully'
				);
				// Reset form
				setTeacherData({ name: '', description: '', photoId: '' });
				setFile(null);
				setTeacherId('');
				setIsEditing(false);
				fetchTeachers();
			} else {
				const errorData = await teacherResponse.json();
				console.error('❌ Error saving teacher:', errorData);
				alert(`Error saving teacher: ${errorData.message || 'Unknown error'}`);
			}
		} catch (error) {
			alert('Error uploading file or form!');
			console.error('❌ Submission error:', error);
		}
	};

	const fetchTeachers = async () => {
		try {
			const response = await fetch(`${baseUrl}/api/v1/teacher`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${Cookie.get('token')}`,
				},
			});
			if (response.ok) {
				const data = await response.json();
				setTeachers(data);
			} else {
				console.error('Failed to fetch teachers');
			}
		} catch (error) {
			console.error('Fetch teachers error:', error);
		}
	};

	const handleGetTeacherData = async (event) => {
		const selectedTeacherId = event.target.value;
		setTeacherId(selectedTeacherId);

		if (!selectedTeacherId) {
			// Reset form if no teacher selected
			setTeacherData({ name: '', description: '', photoId: '' });
			setIsEditing(false);
			setFile(null);
			return;
		}

		try {
			const response = await fetch(
				`${baseUrl}/api/v1/teacher/${selectedTeacherId}`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${Cookie.get('token')}`,
					},
				}
			);

			if (response.ok) {
				const data = await response.json();
				setTeacherData({
					name: data.name || '',
					description: data.description || '',
					photoId: data.photoId || '',
				});
				setFile(null); // Reset file input when loading existing teacher
				setIsEditing(true);
			} else {
				alert('Error loading teacher data');
			}
		} catch (error) {
			console.error('Error loading teacher:', error);
			alert('Error loading teacher data');
		}
	};

	const handleDeleteTeacher = async (event) => {
		event.preventDefault();
		if (!teacherId) {
			alert('Please select a teacher to delete');
			return;
		}

		if (!window.confirm('Are you sure you want to delete this teacher?')) {
			return;
		}

		try {
			const response = await fetch(`${baseUrl}/api/v1/teacher/${teacherId}`, {
				method: 'DELETE',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${Cookie.get('token')}`,
				},
			});

			if (response.ok) {
				alert('Teacher deleted successfully');
				setTeacherId('');
				setTeacherData({ name: '', description: '', photoId: '' });
				setIsEditing(false);
				fetchTeachers();
			} else {
				const errorData = await response.json();
				alert(
					`Error deleting teacher: ${errorData.message || 'Unknown error'}`
				);
			}
		} catch (error) {
			console.error('Delete error:', error);
			alert('Error deleting teacher');
		}
	};

	const handleCancelEdit = () => {
		setTeacherData({ name: '', description: '', photoId: '' });
		setTeacherId('');
		setIsEditing(false);
		setFile(null);
	};

	useEffect(() => {
		fetchTeachers();
	}, []);

	return (
		<section className='flex flex-wrap gap-3 p-4 justify-center items-center mt-36 '>
			<form
				className='bg-white p-6 rounded-lg shadow-md w-full md:w-1/2 border-2 border-main flex flex-wrap gap-3'
				onSubmit={handleSubmitForm}>
				<div className='flex flex-col flex-grow'>
					<label className='text-main'> {t('teacherName')}</label>
					<input
						type='text'
						name='name'
						value={teacherData.name}
						onChange={handleInputsValue}
						placeholder='Teacher Name'
						className='block text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none p-2'
						required
					/>
				</div>
				<div className='flex flex-col flex-grow'>
					<label className='text-main'> {t('description')}</label>
					<textarea
						name='description'
						value={teacherData.description}
						onChange={handleInputsValue}
						placeholder='Description'
						className='block text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none p-2'
						rows={3}
					/>
				</div>
				<div className='flex flex-col flex-grow w-full'>
					<label className='text-main'> {t('uploadFile')}</label>
					<input
						type='file'
						onChange={handleFileChange}
						className='block text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none p-2'
						accept='image/*'
					/>
					{isEditing && teacherData.photoId && !file && (
						<p className='text-sm text-gray-500 mt-1'>
							Current photo will be kept
						</p>
					)}
				</div>

				<div className='flex gap-2 w-full'>
					<button
						type='submit'
						className='flex-1 bg-main text-white py-2 px-4 rounded-lg'>
						{isEditing ? 'Update Teacher' : 'Create New Teacher'}
					</button>
					{isEditing && (
						<button
							type='button'
							onClick={handleCancelEdit}
							className='flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg'>
							Cancel
						</button>
					)}
				</div>
			</form>

			<form className='bg-white p-6 rounded-lg shadow-md w-full md:w-1/2 border-2 border-main flex flex-wrap gap-3'>
				<h2 className='text-main text-2xl font-bold'>{t('editTeacher')}</h2>
				<select
					className='block w-full text-sm text-gray-700 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none mb-4 p-2'
					value={teacherId}
					onChange={handleGetTeacherData}>
					<option value=''>{t('selectEditTeacher')}</option>
					{teachers.map((teacher) => (
						<option
							key={teacher.id}
							value={teacher.id}>
							{teacher.name}
						</option>
					))}
				</select>
				{isEditing && (
					<p className='text-sm text-green-600'>Editing: {teacherData.name}</p>
				)}
			</form>

			<form
				className='bg-white p-6 rounded-lg shadow-md w-full md:w-1/2 border-2 border-red-600 flex flex-wrap gap-3'
				onSubmit={handleDeleteTeacher}>
				<h2 className='text-red-600 text-2xl font-bold'>
					{t('deleteTeacher')}
				</h2>
				<select
					className='block w-full text-sm text-gray-700 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none mb-4 p-2'
					value={teacherId}
					onChange={handleGetTeacherData}>
					<option value=''>{t('selectDeleteTeacher')}</option>
					{teachers.map((teacher) => (
						<option
							key={teacher.id}
							value={teacher.id}>
							{teacher.name}
						</option>
					))}
				</select>
				<button
					type='submit'
					className='w-full bg-red-600 text-white py-2 px-4 rounded-lg'
					disabled={!teacherId}>
					{t('deleteTeacher')}
				</button>
			</form>
		</section>
	);
}

export default AddTeacher;
